import React from 'react';
import { Flex } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { FilterOutlined } from '@ant-design/icons';

import { Button } from '../Button';
import { renderFilterFieldControl, isFilterValueActive } from './filterFieldControl';
import type { FilterFieldControlTexts } from './filterFieldControl';
import type { ColumnConfig, FilterField, FilterValues } from './DataTable.types';

interface BuildColumnFilterPropsArgs<T> {
    col: ColumnConfig<T>;
    filterValues: FilterValues;
    onFilterChange?: (key: string, value: unknown) => void;
    /** Only the two hues the header icon needs — not the full `Colors` object. */
    iconColors: { active: string; inactive: string };
    texts: FilterFieldControlTexts;
    resetLabel: string;
    confirmLabel: string;
}

/**
 * EAA-1666: bridges a column's `.filter` config to antd's native
 * `filterDropdown`/`filterIcon`/`filtered` column props, backed by THIS
 * table's own `filterValues`/`onFilterChange` state rather than antd's
 * internal `selectedKeys` bookkeeping. Keeping the state external means the
 * header icon and the toolbar's per-field button (`TableToolbar.tsx`) read
 * and write the exact same value — one source of truth, two entry points.
 *
 * Returns `{}` (no-op spread) when the column has no `.filter` or the table
 * isn't wired for filtering, so this is purely additive per-column.
 */
export function buildColumnFilterProps<T>({
    col,
    filterValues,
    onFilterChange,
    iconColors,
    texts,
    resetLabel,
    confirmLabel,
}: BuildColumnFilterPropsArgs<T>): Partial<ColumnType<T>> {
    if (!col.filter || !onFilterChange) {
        return {};
    }

    const filterKey = col.filterKey ?? col.key;
    const field: FilterField = { ...col.filter, key: filterKey, columnKey: col.key };
    const active = isFilterValueActive(filterValues[filterKey]);

    return {
        filtered: active,
        filterIcon: (filtered: boolean) => (
            <FilterOutlined
                className="data-table-column-filter-icon"
                style={{ color: filtered || active ? iconColors.active : iconColors.inactive }}
            />
        ),
        filterDropdown: ({ confirm, clearFilters }) => (
            <div className="data-table-column-filter" style={{ padding: 8, width: 240 }}>
                {renderFilterFieldControl(
                    field,
                    filterValues[filterKey],
                    (value) => onFilterChange(filterKey, value),
                    texts
                )}
                <Flex
                    className="data-table-column-filter-actions"
                    justify="space-between"
                    style={{ marginTop: 8 }}
                >
                    <Button
                        className="data-table-column-filter-reset"
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => {
                            onFilterChange(filterKey, null);
                            clearFilters?.();
                        }}
                    >
                        {resetLabel}
                    </Button>
                    <Button
                        className="data-table-column-filter-confirm"
                        type="primary"
                        size="small"
                        onClick={() => confirm()}
                    >
                        {confirmLabel}
                    </Button>
                </Flex>
            </div>
        ),
    };
}
